import { useField, useFormikContext } from 'formik';
import { ChangeEvent, FocusEvent, memo, useEffect, useRef } from 'react';
import { BaseTextField } from '../../../BaseTextField/BaseTextField';
import { useLocale } from '../../../../../context/Localization/localization';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../../context/Keyboard/keyboard.types';
import { useKeyboardIntegration } from '../../../../../context/Keyboard/keyboard';
import { KioskFormTextAreaProps } from '../FormTextArea.types';
import { useScrollToOnError } from '../../../../../helpers/useScrollTo';

export const KioskFormTextArea = memo(
    ({
        placeholder,
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        keyboardLayout,
        shiftAfterSpace,
        shiftAfterPeriod,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
    }: KioskFormTextAreaProps) => {
        const [field, meta] = useField<string>(name);
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         */
        const valueRef = useRef<string>(field.value ?? '');
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         */
        const caretPositionRef = useRef<number | null>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * Maintains synchronization between the external formik value if
             * it gets updated by an external update, so that the internal valueRef
             * gets updated as well in the same render.
             */
            if (valueRef.current !== field.value) {
                valueRef.current = field.value;
                if (inputRef.current) {
                    inputRef.current.value = field.value;
                }
            }
        }, [field.value]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }
        }, [inputRef.current?.value]);

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const getValue = (): GetValueReturn => {
            if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                const selectionStart: number = inputRef.current.selectionStart;
                const selectionEnd: number = inputRef.current.selectionEnd;

                return {
                    inputValue: valueRef.current,
                    caretPositionStart: selectionStart,
                    caretPositionEnd: selectionEnd,
                };
            }
            else {
                return {
                    inputValue: valueRef.current,
                    caretPositionStart: valueRef.current.length,
                    caretPositionEnd: valueRef.current.length,
                };
            }
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const setValue = ({ value, caretPosition }: SetValueProps): void => {
            caretPositionRef.current = caretPosition;
            valueRef.current = value;
            setFieldValue(name, value);
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onKeyboardInput = (): void => {
            if (inputRef.current && !isFocused.current) {
                inputRef.current.focus();
            }
        };

        const { onFocus, onBlur } = useKeyboardIntegration({
            fieldValue: field.value,
            getValue: getValue,
            setValue: setValue,
            onInputChange: onInputChange,
            onKeyboardInput: onKeyboardInput,
            keyboardLayout: keyboardLayout,
            shiftAfterPeriod: shiftAfterPeriod,
            shiftAfterAmpersand: shiftAfterAmpersand,
            shiftAfterSpace: shiftAfterSpace,
            hideKeyboardOnClickOut: hideKeyboardOnClickOut,
            capsLock: capsLock,
        });

        const handleOnBlur = () => {
            onBlur();
            isFocused.current = false;
        };

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            caretPositionRef.current = event.target.selectionEnd;
            valueRef.current = event.target.value;
            setFieldValue(name, event.target.value);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            isFocused.current = true;
            onFocus(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                placeholder={placeholder}
                variant={'filled'}
                multiline={true}
                minRows={6}
                maxRows={6}
                sx={{
                    color: 'white',
                    '& .MuiInputBase-input::placeholder': {
                        color: 'black',
                        opacity: 0.6,
                    },
                    '& .MuiFilledInput-root': {
                        '&:before': {
                            borderBottom: '2px solid',
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: '2px solid',
                        },
                        '&:after': {
                            borderBottom: '2px solid',
                        },
                    },
                }}
            />
        );
    },
);